import { useState, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserShield,
  faCheck,
  faSave,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import Modal from "../../../components/ui/modal";
import { userService } from "../../../services/service";
import { toastSuccess, toastError } from "../../../utils/toast";

const ManagePositionModal = ({
  customer,
  isOpen,
  onClose,
  onSave,
  onUserUpdated,
}) => {
  const [selectedPosition, setSelectedPosition] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const positions = [
    { value: "seller", label: "Seller" },
    { value: "owner", label: "Shop Owner" },
  ];

  useEffect(() => {
    if (customer) {
      const roleMap = {
        Seller: "seller",
        "Shop Owner": "owner",
        seller: "seller",
        owner: "owner",
      };
      setSelectedPosition(roleMap[customer.position] || "seller");
    }
  }, [customer]);

  const handleSave = async () => {
    try {
      setIsLoading(true);

      const roleMap = {
        seller: "seller",
        owner: "owner",
      };
      const role = roleMap[selectedPosition] || "seller";

      const response = await userService.changeRole(customer.userId, role);

      if (response?.status === "success") {
        const positionLabel =
          selectedPosition === "seller" ? "Seller" : "Shop Owner";
        toastSuccess(`Position changed to ${positionLabel} successfully!`);

        const updatedCustomer = {
          ...customer,
          position: positionLabel,
          updated_at: new Date().toISOString().split("T")[0],
        };

        onClose();

        if (onUserUpdated) {
          await onUserUpdated();
        }

        if (onSave) {
          onSave(updatedCustomer);
        }
      }
    } catch (error) {
      console.error("Failed to update position:", error);
      toastError(
        error?.response?.data?.message ||
          "Failed to update position. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Manage Position"
      footer={
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isLoading}
            className="w-full sm:w-auto px-6 border-2 border-orange-700 text-red-600 hover:text-white hover:bg-orange-600 hover:border-none"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isLoading}
            className="gap-2 w-full sm:w-auto bg-blue-800 hover:bg-blue-900 text-white px-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="h-4 w-4 animate-spin"
                />
                Saving...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} />
                Save position
              </>
            )}
          </Button>
        </div>
      }
    >
      {customer && (
        <div className="flex flex-col gap-4 text-sm">
          <div className="flex items-center gap-3 p-3 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <FontAwesomeIcon
                icon={faUserShield}
                className="text-blue-800 h-4 w-4"
              />
            </div>
            <div>
              <p className="text-sm text-orange-600 font-semibold">
                Change position
              </p>
              <p className="font-medium text-gray-900">{customer.full_name}</p>
              <p className="text-xs text-gray-600">
                Current: {customer.position}
              </p>
            </div>
          </div>

          <p className="text-gray-600">
            Select new position for{" "}
            <span className="font-semibold text-gray-900">
              {customer.full_name}
            </span>
          </p>

          <div className="flex flex-col gap-2">
            {positions.map((position) => (
              <button
                key={position.value}
                onClick={() => setSelectedPosition(position.value)}
                disabled={isLoading}
                className={`flex items-center justify-between px-4 py-3 rounded-md border transition-colors ${
                  selectedPosition === position.value
                    ? "border-2 border-blue-800 bg-blue-50 text-blue-800"
                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <div className="flex items-center gap-3">
                  <FontAwesomeIcon
                    icon={faUserShield}
                    className={`h-4 w-4 ${
                      selectedPosition === position.value
                        ? "text-blue-800"
                        : "text-gray-500"
                    }`}
                  />
                  <span className="font-medium">{position.label}</span>
                </div>
                {selectedPosition === position.value && (
                  <FontAwesomeIcon
                    icon={faCheck}
                    className="h-4 w-4 text-blue-800"
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ManagePositionModal;
